import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { TPartImage } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";
import { createPartDefaultImageUpdater } from "~/lib/cache-update-utils";

import type { SessionCacheData } from "../use-session";

// Type for the mutation input variables
interface SetDefaultPartImageVariables {
    partId: string;
    imageId: string;
    sessionId?: string;
    partInteractionId?: string; // AIDEV-NOTE: Optional - if provided, sets default for interaction instead of part
}

// Type for the expected API response
interface SetDefaultPartImageResponse {
    success: boolean;
    image: TPartImage & { signedUrl: string };
}

/**
 * Hook to set the default image for a part.
 */
export function useSetDefaultPartImage(sessionToken?: string | null) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<
        SetDefaultPartImageResponse,
        Error,
        SetDefaultPartImageVariables
    >({
        mutationFn: async ({ partId, imageId, partInteractionId }) => {
            // AIDEV-NOTE: partId and partInteractionId now sent in request body
            const response = await apiClient.post(
                `/part/image/${imageId}/set-default`,
                { partId, ...(partInteractionId ? { partInteractionId } : {}) },
            );

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    // Ignore
                }
                const errorMessage =
                    errorData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            return response.json() as Promise<SetDefaultPartImageResponse>;
        },
        onSuccess: (data, variables) => {
            // Directly update the parts list in the session cache
            if (!variables.sessionId) {
                // Handle missing sessionId, perhaps log an error or do nothing
                console.error(
                    "SessionId is missing, cannot update cache for default image.",
                );
                // Alternatively, show a toast to the user if this is unexpected
                // toast({ title: "Cache Update Skipped", description: "Session ID was not available.", variant: "destructive" });
                return; // Exit if no sessionId to prevent errors with queryKey
            }

            const updater = createPartDefaultImageUpdater(
                variables.partId,
                variables.imageId,
                data.image, // data.image is TPartImage & { signedUrl: string }, compatible with TPartImage
            );
            queryClient.setQueryData<SessionCacheData | undefined>(
                ["session", variables.sessionId, sessionToken],
                updater,
            );

            // Also invalidate the list of images, in case the UI indicates the default differently
            queryClient.invalidateQueries({
                queryKey: ["partImages", variables.partId],
            });
            // And invalidate the specific image status query, if active
            queryClient.invalidateQueries({
                queryKey: [
                    "partImageStatus",
                    variables.partId,
                    variables.imageId,
                ],
            });
            // AIDEV-NOTE: Invalidate all parts-related queries to ensure consistency across pages
            queryClient.invalidateQueries({
                queryKey: ["parts"],
            });
            // Also invalidate session queries that might contain parts data
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey;
                    return (
                        Array.isArray(key) &&
                        (key.includes("session") ||
                            key.includes("parts") ||
                            key.includes("part"))
                    );
                },
            });

            toast({
                title: "Default Image Set",
                description:
                    "The default image for this part has been updated.",
            });
        },
        onError: (error) => {
            toast({
                title: "Update Failed",
                description: error.message || "Could not set default image.",
                variant: "destructive",
            });
        },
    });
}
